import { type ColumnDef } from "@tanstack/react-table";

import { formatDateTimeCompact } from "@/utils/relative-time";

import { EMPTY_VALUE, SYSTEM_USER_LABEL } from "./constants";
import { type FarmerIdentificationColumnMeta } from "./types";
import { type FarmerIdentificationType, type UserSummary } from "../../types";

export const getColumnMeta = (columnDef: ColumnDef<FarmerIdentificationType, unknown>) =>
  columnDef.meta as FarmerIdentificationColumnMeta | undefined;

export const getEntityKey = (value: FarmerIdentificationType["id"]) =>
  value === undefined || value === null ? undefined : String(value);

export const getRowId = (row: FarmerIdentificationType, index: number) =>
  getEntityKey(row.id) ?? `${index}-${row.name ?? "farmer-identification"}`;

export const getName = (identificationType: FarmerIdentificationType) =>
  identificationType.name?.trim() || "";

export const getCode = (identificationType: FarmerIdentificationType) =>
  identificationType.code?.trim() || "";

export const getTimestamp = (value: string | null | undefined) => {
  if (!value) {
    return undefined;
  }

  const timestamp = new Date(value).getTime();

  return Number.isNaN(timestamp) ? undefined : timestamp;
};

export const getFullName = (user?: UserSummary | null) => {
  const fullName = [user?.first_name, user?.last_name]
    .map(name => name?.trim())
    .filter(Boolean)
    .join(" ");

  return fullName || SYSTEM_USER_LABEL;
};

export const formatAuditDateTime = (value?: string | null) =>
  formatDateTimeCompact(value) || EMPTY_VALUE;

export const getLengthRange = (identificationType: FarmerIdentificationType) => {
  const { value_maximum_length: maximum, value_minimum_length: minimum } = identificationType;

  if (minimum === null || minimum === undefined || maximum === null || maximum === undefined) {
    return EMPTY_VALUE;
  }

  return `${minimum} – ${maximum}`;
};

const normalizeSearchValue = (value: string) => value.trim().toLocaleLowerCase();

export const filterFarmerIdentification = (
  identificationTypes: FarmerIdentificationType[],
  searchTerm?: string
) => {
  const query = normalizeSearchValue(searchTerm ?? "");

  return query
    ? identificationTypes.filter(identificationType =>
        normalizeSearchValue(getName(identificationType)).includes(query)
      )
    : identificationTypes;
};
